import { ReactNode } from "react";

export const gettingStartedContent: Record<string, ReactNode> = {
  "getting-started-dashboard": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Dashboard Overview</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Your business at a glance — revenue, expenses, and per-vehicle performance in one view.
      </p>

      <p>
        The Dashboard is the first screen you see after logging in. It provides a high-level summary of your Turo business, combining earnings and expense data into charts and vehicle performance cards.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">What You'll See</h2>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Monthly Revenue Chart</h3>
      <p>
        A bar chart showing your <strong>monthly earnings over time</strong>. This updates automatically as you import new Turo earnings CSVs. Use it to spot seasonal trends, growth patterns, or months where revenue dipped.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Vehicle Performance Cards</h3>
      <p>
        Below the chart, each vehicle in your fleet gets its own card showing:
      </p>
      <ul>
        <li><strong>Total revenue</strong> earned from trips</li>
        <li><strong>Total expenses</strong> logged against that vehicle</li>
        <li><strong>Net profit</strong> (revenue minus expenses)</li>
        <li><strong>ROI progress</strong> — how much of the vehicle's purchase price has been recovered</li>
      </ul>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Tip</p>
        <p className="mb-0">Vehicles with negative net profit are highlighted so you can quickly identify which cars need attention.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Getting Data on the Dashboard</h3>
      <p>
        The Dashboard relies on data from two sources:
      </p>
      <ol>
        <li><strong>Earnings</strong> — Import your Turo CSV to populate revenue data</li>
        <li><strong>Expenses</strong> — Log expenses manually or via CSV import</li>
      </ol>
      <p>
        If you haven't imported any data yet, the Dashboard will prompt you to import your first earnings CSV. See the <em>Importing Earnings</em> and <em>Tracking Expenses</em> guides for step-by-step instructions.
      </p>
    </div>
  ),

  "getting-started-earnings": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Importing Earnings</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Bring your Turo trip data into HostCaptain with a simple CSV import.
      </p>

      <p>
        HostCaptain imports your earnings directly from the CSV file that Turo provides. This gives you detailed trip-by-trip data including guest names, dates, daily pricing, fees, distance traveled, and net earnings — all organized and ready for analysis.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">How to Download Your CSV from Turo</h2>

      <div className="bg-card border rounded-lg p-5 my-6">
        <p className="font-semibold text-lg mb-3">Step-by-step:</p>
        <ol className="space-y-2 mb-0">
          <li>Log in to your <strong>Turo host account</strong> on the web (not the mobile app)</li>
          <li>Navigate to <strong>Business → Earnings</strong> from the top menu</li>
          <li>Select the date range you want to export (we recommend exporting all available history)</li>
          <li>Click the <strong>Download CSV</strong> button</li>
          <li>Save the file to your computer</li>
        </ol>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Important</p>
        <p className="mb-0">Make sure to download the CSV from the <strong>web browser</strong>, not the Turo mobile app. The mobile app doesn't provide the full CSV export.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Importing Into HostCaptain</h2>

      <ol className="space-y-3">
        <li>Go to the <strong>Earnings</strong> page in HostCaptain</li>
        <li>Click the <strong>"Import CSV"</strong> button</li>
        <li>Select or drag-and-drop your Turo CSV file</li>
        <li>HostCaptain will validate the file format and show you a preview of the data</li>
        <li>Click <strong>"Import"</strong> to confirm</li>
      </ol>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Smart Duplicate Handling</p>
        <p className="mb-0">If you import a CSV that contains trips you've already imported, HostCaptain will <strong>update existing records</strong> rather than creating duplicates. This means you can safely re-import your full earnings history at any time without worrying about double-counting.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Vehicle Matching</h2>

      <p>
        When you import earnings, HostCaptain automatically tries to <strong>match trips to vehicles</strong> in your fleet:
      </p>
      <ol>
        <li><strong>Nickname match</strong> — The vehicle name in the CSV is compared against your vehicle names in HostCaptain</li>
        <li><strong>VIN match</strong> — If no nickname match is found, the VIN from the CSV is compared against your vehicle VINs</li>
      </ol>
      <p>
        For the best results, make sure your vehicles in HostCaptain have the same name or VIN as they appear on Turo. You can check and update this on the <em>Vehicles</em> page.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">What Gets Imported</h2>

      <p>Each trip record includes:</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-6">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Trip Details</p>
          <ul className="text-sm mb-0 space-y-0.5">
            <li>Reservation ID</li>
            <li>Guest name</li>
            <li>Start & end dates</li>
            <li>Trip duration (days)</li>
            <li>Trip status</li>
          </ul>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Financial Details</p>
          <ul className="text-sm mb-0 space-y-0.5">
            <li>Trip price & boost price</li>
            <li>All discount types</li>
            <li>Delivery, extras, fees</li>
            <li>Turo service fees</li>
            <li>Total / net earnings</li>
          </ul>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Vehicle Details</p>
          <ul className="text-sm mb-0 space-y-0.5">
            <li>Vehicle name (Turo nickname)</li>
            <li>Turo vehicle ID</li>
            <li>VIN</li>
          </ul>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Odometer & Extras</p>
          <ul className="text-sm mb-0 space-y-0.5">
            <li>Check-out / check-in odometer</li>
            <li>Distance traveled</li>
            <li>Pickup & return locations</li>
          </ul>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Viewing Your Earnings</h2>

      <p>
        After importing, the Earnings page shows a <strong>sortable, searchable table</strong> of all your trips. You can filter by vehicle, date range, or trip status. Summary statistics at the top show total earnings, average per trip, and monthly trends.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Clearing Earnings Data</h3>
      <p>
        If you need to start fresh, you can clear all imported earnings from the Earnings page. This removes all trip records and lets you re-import a clean dataset. This action is permanent and cannot be undone.
      </p>
    </div>
  ),

  "getting-started-expenses": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Tracking Expenses</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Know exactly where your money goes — per vehicle and across your entire operation.
      </p>

      <p>
        Expense tracking is essential for understanding your <strong>true profitability</strong>. Turo shows you revenue, but it doesn't account for insurance, cleaning, maintenance, loan payments, or any of the other costs that eat into your margins. HostCaptain fills that gap.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Adding Expenses</h2>

      <p>There are two ways to add expenses:</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">1. Quick Add (Manual Entry)</h3>
      <p>
        Click the <strong>"Add Expense"</strong> button on the Expenses page to open the quick-add form. Fill in:
      </p>
      <ul>
        <li><strong>Expense name</strong> — A short description (e.g., "Oil change", "Monthly insurance")</li>
        <li><strong>Category</strong> — The type of expense (maintenance, insurance, cleaning, etc.)</li>
        <li><strong>Amount</strong> — The cost</li>
        <li><strong>Date</strong> — When the expense occurred</li>
        <li><strong>Vehicle</strong> — Which car it applies to, or <strong>"Operations"</strong> for company-wide costs</li>
        <li><strong>Expense type, Paid by, Paid to</strong> — Optional fields for more detailed tracking</li>
        <li><strong>Notes</strong> — Any additional context</li>
      </ul>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">2. CSV Import</h3>
      <p>
        For bulk entry, you can import expenses from a CSV file. Click <strong>"Import CSV"</strong> and upload a file with columns matching the expense fields. A sample CSV template is available for download inside the import dialog.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Tip</p>
        <p className="mb-0">Download the sample CSV first to see the exact format expected. This avoids import errors from column name mismatches.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The "Operations" Category</h2>

      <p>
        Not every expense is tied to a specific vehicle. Company-wide costs like <strong>LLC registration fees, office supplies, accounting software, or general parking</strong> should be assigned to <strong>"Operations"</strong> instead of a specific car.
      </p>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold mb-2">How it works:</p>
        <ul className="mb-0">
          <li>When adding an expense, select <strong>"Operations"</strong> from the vehicle dropdown</li>
          <li>Operations expenses appear in their own section in the expense summary</li>
          <li>They are included in your total business expenses but not attributed to any single vehicle's P&L</li>
        </ul>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Note</p>
        <p className="mb-0">"Operations" is a reserved name — you cannot create a vehicle called "Operations" to avoid confusion.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Expense Summary Cards</h2>

      <p>
        At the top of the Expenses page, summary cards give you an at-a-glance view:
      </p>
      <ul>
        <li><strong>Total Expenses</strong> — Sum of all expenses across all vehicles and operations</li>
        <li><strong>Average Expenses per Car</strong> — Total vehicle expenses divided by the number of vehicles</li>
        <li><strong>Total Operations Expenses</strong> — Sum of all company-wide costs</li>
        <li><strong>Per-vehicle totals</strong> — Individual cards showing total expenses for each car</li>
      </ul>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Customizing Your Expense Table</h2>

      <p>
        The expense table supports <strong>custom columns</strong> and <strong>custom dropdown options</strong>. You can:
      </p>
      <ul>
        <li>Add custom columns with different data types (text, number, dropdown)</li>
        <li>Customize the options available in the "Expense Type" and "Paid By" dropdowns</li>
        <li>Reorder columns to match your preferred workflow</li>
      </ul>
      <p>
        Access these settings via the <strong>"Manage Options"</strong> button on the Expenses page.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Editing & Deleting Expenses</h2>

      <p>
        Click on any cell in the expense table to <strong>edit it inline</strong>. Changes are saved automatically. To delete an expense, use the delete action in the row's action menu. You can also clear all expenses at once if you need to start fresh.
      </p>
    </div>
  ),

  "getting-started-vehicles": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Managing Vehicles</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Your fleet is the foundation — keep it organized and every other feature works better.
      </p>

      <p>
        The Vehicles page is where you manage every car in your fleet. Accurate vehicle records are important because they link to your earnings, expenses, and maintenance data — giving you a complete picture of each car's performance.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Adding a Vehicle</h2>

      <p>Click <strong>"Add Vehicle"</strong> and fill in the details:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-6">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Required Fields</p>
          <ul className="text-sm mb-0 space-y-0.5">
            <li>Vehicle name (nickname)</li>
            <li>Year</li>
            <li>Make</li>
            <li>Model</li>
          </ul>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Optional Fields</p>
          <ul className="text-sm mb-0 space-y-0.5">
            <li>Trim & color</li>
            <li>VIN & license plate</li>
            <li>Turo vehicle ID</li>
            <li>Purchase price & date</li>
            <li>Current mileage</li>
            <li>Photo</li>
          </ul>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Matching Tip</p>
        <p className="mb-0">Set your vehicle <strong>name</strong> to match the nickname on Turo, and add the <strong>VIN</strong>. This ensures earnings imports automatically link trips to the right vehicle.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Vehicle Status</h2>

      <p>Each vehicle has a status that helps you track your fleet at a glance:</p>
      <ul>
        <li><strong>Active</strong> — Currently listed and available on Turo</li>
        <li><strong>Inactive</strong> — Temporarily off the platform (e.g., in the shop, seasonal break)</li>
        <li><strong>Sold</strong> — No longer part of your fleet</li>
      </ul>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Vehicle Performance</h2>

      <p>
        Each vehicle card on the Vehicles page shows key financial metrics pulled from your imported earnings and logged expenses:
      </p>
      <ul>
        <li><strong>Total revenue</strong> from all trips</li>
        <li><strong>Total expenses</strong> logged against the vehicle</li>
        <li><strong>Net profit</strong> (revenue − expenses)</li>
        <li><strong>ROI progress</strong> — percentage of the purchase price recovered</li>
      </ul>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Merging Duplicate Vehicles</h2>

      <p>
        If you accidentally created duplicate records for the same car (for example, one from a CSV import and one added manually), you can <strong>merge them</strong>. Merging transfers all trips, expenses, and maintenance tasks from the duplicate to the primary vehicle, then deletes the duplicate.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Deleting a Vehicle</h2>

      <p>
        Deleting a vehicle will remove it and all associated data (trips, expenses, maintenance tasks). This action is <strong>permanent and cannot be undone</strong>. If you want to keep historical data but stop tracking the vehicle, set its status to <strong>"Sold"</strong> instead.
      </p>
    </div>
  ),

  "getting-started-recurring-expenses": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Recurring Expenses & Loans</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Set it once and let HostCaptain generate your monthly expenses automatically — no more manual entry for predictable costs.
      </p>

      <p>
        Many Turo hosting costs repeat on a schedule — insurance premiums, car payments, parking fees, subscription services. Instead of manually logging these every month, HostCaptain lets you create <strong>recurring expense templates</strong> that automatically generate expense records at the interval you choose.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">How Recurring Expenses Work</h2>

      <p>A recurring expense is a <strong>template</strong> — not an expense itself. It defines:</p>
      <ul>
        <li><strong>Amount</strong> — How much the expense costs each period</li>
        <li><strong>Expense Type</strong> — The expense type (e.g., Insurance, Parking, Subscription)</li>
        <li><strong>Frequency</strong> — How often it recurs: weekly, bi-weekly, monthly, quarterly, or annually</li>
        <li><strong>Vehicle</strong> — Which car it applies to (or leave blank for Operations expenses)</li>
        <li><strong>Start date</strong> — When the first expense should be generated</li>
        <li><strong>Expense Type, Paid By, Paid To</strong> — Optional fields that carry over to each generated expense</li>
      </ul>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 How it saves you time</p>
        <p className="mb-0">Once created, HostCaptain automatically generates a real expense record at each interval. You never have to remember to log your car insurance, monthly parking, or loan payment again.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Creating a Recurring Expense</h2>

      <ol className="space-y-3">
        <li>Go to the <strong>Expenses</strong> page and click the <strong>"Recurring"</strong> tab</li>
        <li>Click <strong>"Add Recurring Expense"</strong></li>
        <li>Fill in the amount, category, frequency, and start date</li>
        <li>Optionally assign it to a vehicle and set Expense Type, Paid By, and Paid To</li>
        <li>Click <strong>Save</strong></li>
      </ol>

      <p>
        The template will appear in your recurring expenses list. At each scheduled interval, a corresponding expense record is automatically created in your main Expenses table with all the details filled in.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Editing & Overriding Values</h2>

      <p>
        You can edit a recurring expense template at any time. Changes apply to <strong>future generated expenses only</strong> — previously generated records are not affected. This means you can:
      </p>
      <ul>
        <li>Update the amount if a price changes (e.g., insurance renewal at a new rate)</li>
        <li>Change the Paid By or Expense Type for better categorization going forward</li>
        <li>Pause a template by toggling it inactive, then reactivate later</li>
      </ul>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Loans</h2>

      <p>
        The <strong>Loans</strong> tab (also on the Expenses page) is designed for vehicle financing — car loans, personal loans used to buy a vehicle, or any debt tied to your fleet.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Adding a Loan</h3>
      <p>When you create a loan, you provide:</p>
      <ul>
        <li><strong>Lender name</strong> — Who you're paying (e.g., "Chase Auto", "Capital One")</li>
        <li><strong>Original amount</strong> — The total loan principal</li>
        <li><strong>Interest rate</strong> — Annual percentage rate (APR)</li>
        <li><strong>Term</strong> — Loan length in months</li>
        <li><strong>Monthly payment</strong> — Your fixed payment amount</li>
        <li><strong>Origination date</strong> — When the loan started</li>
        <li><strong>Vehicle</strong> — Which car the loan is for</li>
        <li><strong>Expense Type, Paid By, Paid To</strong> — Optional metadata that flows into generated expenses</li>
      </ul>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Automatic Interest & Principal Split</h3>
      <p>
        When you create a loan, HostCaptain automatically creates <strong>two recurring expense templates</strong>:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-6">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Interest Payment</p>
          <p className="text-sm mb-0">The interest portion of your monthly payment, calculated from your loan amount and APR. Tracked as loan payment type "Interest".</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold mb-1 text-sm">Principal Paydown</p>
          <p className="text-sm mb-0">The remainder of your monthly payment that reduces your loan balance. Tracked as loan payment type "Principal Paydown".</p>
        </div>
      </div>

      <p>
        These two templates work together: each month, HostCaptain generates two expense records — one for interest and one for principal — so your books accurately reflect where your money goes.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Why this matters</p>
        <p className="mb-0">Separating interest from principal is important for tax purposes and for understanding your true cost of ownership. Interest is typically a deductible business expense, while principal payments are not.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Loan Payoff Tracking</h3>
      <p>
        HostCaptain tracks your loan's remaining balance over time. When the outstanding balance reaches zero, the loan status is automatically updated to <strong>"Paid Off"</strong> and the associated recurring expense templates are deactivated — no more auto-generated payments after the loan is done.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Complete Data Flow</h2>

      <div className="bg-card border rounded-lg p-5 my-6">
        <p className="font-semibold text-lg mb-3">How it all connects:</p>
        <ol className="space-y-2 mb-0">
          <li><strong>You create a Loan</strong> with lender details, terms, and optional metadata (Expense Type, Paid By, Paid To)</li>
          <li><strong>HostCaptain creates Recurring Expense templates</strong> — one for Interest, one for Principal — copying all metadata from the loan</li>
          <li><strong>Each month, real Expense records are generated</strong> from those templates, with all fields pre-populated including vehicle, expense type, amount, and metadata</li>
          <li><strong>These expenses appear in your main Expenses table</strong>, in your Dashboard charts, and in Captain AI's analysis context</li>
        </ol>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Good to know</p>
        <p className="mb-0">If you edit a loan (e.g., change the lender name or interest rate), the associated recurring expense templates are automatically updated and recalculated. However, expenses that were already generated are not retroactively changed.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Common Use Cases</h2>

      <div className="space-y-2 my-6">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🚗 Car loan payments — Create a loan and let HostCaptain split interest vs. principal automatically</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🛡️ Monthly insurance — Set up a recurring expense for each vehicle's insurance premium</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🅿️ Monthly parking — Recurring expense assigned to Operations or a specific vehicle</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">📱 Software subscriptions — Turo tools, GPS trackers, or fleet management add-ons</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🧹 Scheduled cleaning — If you pay a cleaning service on a regular schedule</p>
        </div>
      </div>
    </div>
  ),

  "getting-started-maintenance": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Maintenance Tracking</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Stay on top of upkeep to avoid costly breakdowns and keep your fleet in top shape.
      </p>

      <p>
        Regular maintenance is critical for Turo hosts. A breakdown during a guest's trip can lead to bad reviews, damage claims, and expensive towing. HostCaptain's maintenance tracker helps you stay proactive instead of reactive.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Creating Maintenance Tasks</h2>

      <p>Click <strong>"Add Task"</strong> on the Maintenance page and fill in:</p>
      <ul>
        <li><strong>Task name</strong> — What needs to be done (e.g., "Oil Change", "Tire Rotation")</li>
        <li><strong>Vehicle</strong> — Which car needs the work</li>
        <li><strong>Due date</strong> — When it should be completed by</li>
        <li><strong>Due at mileage</strong> — Optional mileage-based trigger (e.g., next oil change at 75,000 miles)</li>
        <li><strong>Description</strong> — Additional notes or instructions</li>
        <li><strong>Attachments</strong> — Upload receipts, invoices, or photos</li>
      </ul>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Task Timeline</h2>

      <p>
        Maintenance tasks are organized in a <strong>visual timeline per vehicle</strong>. This makes it easy to see what's coming up, what's overdue, and what's been completed. Tasks are sorted by due date, with overdue items highlighted.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Tip</p>
        <p className="mb-0">Use both date-based and mileage-based triggers. Some maintenance (like oil changes) is best tracked by mileage, while others (like annual inspections) are date-based.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Completing Tasks</h2>

      <p>
        When a task is done, mark it as <strong>completed</strong>. The completion date is recorded automatically. Completed tasks remain visible in the timeline for your records but are visually distinguished from pending tasks.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Common Maintenance Tasks for Turo Hosts</h2>

      <div className="space-y-2 my-6">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🔧 Oil changes — Every 5,000–7,500 miles depending on the vehicle</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🛞 Tire rotations — Every 5,000–8,000 miles</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🧹 Interior deep clean — Monthly or after every 5 trips</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🔍 State inspection — Annually (varies by state)</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🧊 Wiper blades — Every 6–12 months</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-semibold text-sm mb-0">🔋 Battery check — Every 12 months</p>
        </div>
      </div>
    </div>
  ),

  "getting-started-captain-ai": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Captain AI Assistant</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Your personal AI co-pilot for navigating Turo hosting questions and decisions.
      </p>

      <p>
        Captain AI (called "The Captain") is HostCaptain's built-in AI assistant. It's designed to help you with Turo-specific questions, business decisions, and platform guidance — all in a conversational format.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">What The Captain Can Help With</h2>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🧭 Getting Started</p>
          <p className="mb-0">Not sure where to begin? Ask The Captain for a walkthrough of HostCaptain's features or for advice on your first steps as a Turo host.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📊 Business Questions</p>
          <p className="mb-0">Ask about pricing strategies, when to add another vehicle, how to handle slow seasons, or how to evaluate if a car is profitable.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">⚙️ Platform Help</p>
          <p className="mb-0">Need help importing earnings, setting up expenses, or understanding a feature? The Captain can walk you through it.</p>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🛡️ Handling Situations</p>
          <p className="mb-0">Dealing with a guest dispute, damage claim, or late return? Describe the situation and get step-by-step guidance.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">How It Works</h2>

      <ol className="space-y-3">
        <li>Navigate to <strong>Captain AI</strong> from the sidebar</li>
        <li>Type your question or describe your situation in the chat input</li>
        <li>The Captain will respond with relevant advice, instructions, or explanations</li>
        <li>Continue the conversation — follow-up questions are encouraged</li>
      </ol>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Persistent Conversation</p>
        <p className="mb-0">Your conversation with The Captain is <strong>saved automatically</strong>. You can navigate away and come back later — your chat history will be right where you left it.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Tips for Best Results</h2>

      <ul>
        <li><strong>Be specific</strong> — Instead of "How do I make money?", try "I have a 2020 Toyota Camry listed at $45/day and averaging 15 trips per month. Is that good?"</li>
        <li><strong>Provide context</strong> — Mention your fleet size, location, or specific situation for more tailored advice</li>
        <li><strong>Ask follow-ups</strong> — If the first response is too general, ask The Captain to go deeper</li>
      </ul>
    </div>
  ),

  "getting-started-team-settings": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Team & Settings</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        Manage your account, invite team members, and customize your experience.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Team Management</h2>

      <p>
        If you work with partners, assistants, or operations staff, you can invite them to your HostCaptain company account. This gives them access to the same vehicles, earnings, expenses, and maintenance data.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Inviting Team Members</h3>
      <ol>
        <li>Go to the <strong>Team</strong> page from the sidebar</li>
        <li>Click <strong>"Invite Member"</strong></li>
        <li>Enter their email address</li>
        <li>They'll receive an invitation email with a link to join your company</li>
      </ol>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Note</p>
        <p className="mb-0">Invitations expire after <strong>7 days</strong>. If the invitation isn't accepted in time, you can send a new one.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Roles</h3>
      <ul>
        <li><strong>Owner</strong> — Full access including team management, company settings, and billing</li>
        <li><strong>Member</strong> — Access to all operational data (vehicles, earnings, expenses, maintenance) but cannot manage team members or billing</li>
      </ul>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Settings</h2>

      <p>The Settings page lets you manage:</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Profile</h3>
      <p>Update your display name and profile photo.</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Company</h3>
      <p>Change your company name (visible to team members).</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Subscription</h3>
      <p>
        View your current plan, upgrade to Pro or Scale, or manage your billing through the billing portal. Annual plans offer a discount compared to monthly billing.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Feedback</h3>
      <p>
        Found a bug or have a feature request? Use the feedback form on the Settings page to let us know. You can include screenshots or attachments to help us understand the issue.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Account Deletion</h2>

      <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 my-4">
        <p className="font-semibold text-destructive mb-1">⚠️ Permanent Action</p>
        <p className="mb-0">Deleting your account removes all your data — vehicles, earnings, expenses, maintenance tasks, and conversation history. This action <strong>cannot be undone</strong>. If you're the company owner, the entire company and all its data will be deleted.</p>
      </div>
    </div>
  ),
};
